import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Briefcase, Users, Timer, RefreshCcw, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { STATUS_LABELS } from "../../lib/api";
import { StatusBadge } from "../../components/StatusTools";

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [cases, setCases] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [digestBusy, setDigestBusy] = useState(false);

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(`Digest sent to ${data.sent} doctor(s) · ${data.skipped} skipped · ${data.opted_in} opted in`);
    } catch { toast.error("Digest send failed"); }
    finally { setDigestBusy(false); }
  };

  const load = (s = search, st = status) => {
    const params = {};
    if (s) params.search = s;
    if (st && st !== "all") params.status = st;
    api.get("/admin/cases", { params }).then((r) => setCases(r.data)).catch(() => setCases([]));
  };

  useEffect(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    load("", "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; api/load are stable
  }, []);

  const cards = metrics ? [
    { icon: Briefcase, label: "Total cases", value: metrics.total_cases },
    { icon: Timer, label: "Avg. turnaround", value: metrics.avg_turnaround_days != null ? `${metrics.avg_turnaround_days}d` : "—" },
    { icon: Users, label: "Client accounts", value: metrics.total_clients },
    { icon: RefreshCcw, label: "Open revisions", value: metrics.open_revisions },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-display font-black text-3xl tracking-tight">Case Operations</h1>
        <Button onClick={sendDigest} disabled={digestBusy} data-testid="admin-send-digest-btn" variant="outline"
          className="rounded-full font-bold border-slate-300">
          <Mail className="h-4 w-4 mr-2" /> {digestBusy ? "Sending…" : "Send weekly digest now"}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`metric-${c.label.toLowerCase().replace(/\s|\./g, "-")}`}>
            <c.icon className="h-5 w-5 text-sky-600 mb-3" />
            <p className="font-display font-black text-3xl">{c.value}</p>
            <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} data-testid="admin-search-input"
            onChange={(e) => { setSearch(e.target.value); load(e.target.value, status); }}
            placeholder="Search Case ID, doctor, clinic or patient ref…" className="pl-10 h-11 bg-white" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); load(search, v); }}>
          <SelectTrigger className="w-56 h-11 bg-white" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-cases-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3.5 font-semibold">Case ID</th>
                <th className="px-5 py-3.5 font-semibold">Doctor</th>
                <th className="px-5 py-3.5 font-semibold">Clinic</th>
                <th className="px-5 py-3.5 font-semibold">Patient Ref</th>
                <th className="px-5 py-3.5 font-semibold">Status</th>
                <th className="px-5 py-3.5 font-semibold">Assigned</th>
                <th className="px-5 py-3.5 font-semibold">Updated</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {(cases || []).map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`admin-case-row-${c.case_id}`}>
                  <td className="px-5 py-4 font-mono font-semibold text-sky-700">{c.case_id}</td>
                  <td className="px-5 py-4">{c.client_name}</td>
                  <td className="px-5 py-4 text-slate-500">{c.clinic_name || "—"}</td>
                  <td className="px-5 py-4 text-slate-500">{c.patient_ref}</td>
                  <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                  <td className="px-5 py-4 text-slate-500 text-xs">{c.assigned_to || <span className="text-amber-600 font-semibold">Unassigned</span>}</td>
                  <td className="px-5 py-4 text-slate-500 text-xs">{new Date(c.updated_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4 text-right">
                    <Link to={`/admin/cases/${c.id}`} data-testid={`admin-case-open-${c.case_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-800">
                      Manage <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
              {cases && cases.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-400" data-testid="admin-cases-empty">No cases match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
